import { REFRESH_CONFIG } from '@/constant/index'

export class RefreshManager {
  private static instance: RefreshManager
  private refreshTimer: number | null = null
  private refreshCallback: (() => Promise<void>) | null = null
  private isRunning = false
  
  private constructor() {}

  static getInstance(): RefreshManager {
    if (!RefreshManager.instance) {
      RefreshManager.instance = new RefreshManager()
    }
    return RefreshManager.instance
  }

  /**
   * 设置刷新回调函数
   * @param callback 刷新回调函数
   */
  setRefreshCallback(callback: () => Promise<void>): void {
    this.refreshCallback = callback
  }

  /**
   * 启动定时刷新
   * @param interval 刷新间隔（毫秒），可选，默认使用配置中的间隔
   */
  start(interval?: number): void {
    if (this.isRunning) {
      console.warn('刷新管理器已经在运行中')
      return
    }

    if (!this.refreshCallback) {
      console.error('刷新回调函数未设置')
      return
    }

    const refreshInterval = interval || REFRESH_CONFIG.REFRESH_INTERVAL
    
    this.refreshTimer = setInterval(() => {
      this.executeRefresh()
    }, refreshInterval)
    
    this.isRunning = true
  }

  /**
   * 停止定时刷新
   */
  stop(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
      this.isRunning = false
    }
  }

  /**
   * 执行刷新
   */
  private async executeRefresh(): Promise<void> {
    if (!this.refreshCallback) {
      console.error('刷新回调函数未设置')
      return
    }

    // 检查页面可见性
    if (REFRESH_CONFIG.REFRESH_ONLY_WHEN_VISIBLE) {
      if (document.visibilityState !== 'visible') {
        return
      }
    }

    try {
      await this.refreshCallback()
    } catch (error) {
      console.error('刷新管理器：刷新失败', error)
    }
  }

  /**
   * 手动刷新
   */
  async manualRefresh(): Promise<void> {
    await this.executeRefresh()
  }

  /**
   * 更新刷新间隔
   * @param interval 新的刷新间隔（毫秒）
   */
  updateInterval(interval: number): void {
    if (this.isRunning) {
      this.stop()
      this.start(interval)
    }
  }

  /**
   * 检查是否正在运行
   */
  isRefreshRunning(): boolean {
    return this.isRunning
  }

  /**
   * 获取当前刷新间隔
   */
  getCurrentInterval(): number {
    return REFRESH_CONFIG.REFRESH_INTERVAL
  }
}

// 导出单例实例
export const refreshManager = RefreshManager.getInstance()

// 导出配置更新函数
export const updateRefreshConfig = (config: Partial<typeof REFRESH_CONFIG>) => {
  Object.assign(REFRESH_CONFIG, config)
}

// 导出便捷函数
export const setRefreshInterval = (minutes: number) => {
  const interval = minutes * 60 * 1000
  updateRefreshConfig({ REFRESH_INTERVAL: interval })
  if (refreshManager.isRefreshRunning()) {
    refreshManager.updateInterval(interval)
  }
} 